import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import apiClient from '@/lib/apiClient';
import type { RootState } from '@/store';

export interface QARun {
  model: string;
  answer: string;
  targetMentioned: boolean;
  sources: { uri?: string; title?: string }[];
  searchQueries: string[];
  generatedAt: string; // ISO date
}

// PersonaQA stores model-keyed answers and model-keyed sources.
// The raw API provides `question`, `answers`, and `sources`.
// - answers can be an object keyed by model name: { [modelName]: string }
// - sources is an object keyed by model name where each value is an array of source items:
//     { [modelName]: Array<{ uri?: string; url?: string; title?: string }> }
// We normalize that into `answersByModel` and `sourcesByModel` (mapping url -> uri).
export interface PersonaQA {
  question: string;
  // Store only model-keyed answers (e.g., { gemini: '...', openai: '...' })
  answersByModel: Record<string, string>;
  // Store sources keyed by model, each as { uri?, title?: string }[] (url normalized to uri).
  sourcesByModel?: Record<string, { uri?: string; title?: string }[]>;
  // For each model, whether the target brand/company was mentioned in the answer
  targetMentionedByModel?: Record<string, boolean>;
  // Unique id for this question within a domain/persona (used for deep links)
  qaId?: string;
  // Question generation source: 'gsc_derived' = from real GSC queries, 'ai_generated' = AI invented
  source?: 'ai_generated' | 'gsc_derived';
  // Question intent (data-driven only): informational or transactional
  intent?: 'informational' | 'transactional';
  // Query fan-out: the actual search queries each model sent to the web
  searchQueriesByModel?: Record<string, string[]>;
  // All (model, result) entries from every analysis run in the date window
  runs?: QARun[];
}
export interface PersonaContext {
  role_or_identity?: string;
  industry?: string;
  environment_scale?: string;
  location?: string;
}

export interface ProductService {
  name?: string;
  description?: string;
  features_and_differentiators?: string[];
  pricing?: string[];
}

export interface PersonaParameters {
  target_industry?: string;
  product_service?: ProductService;
  location?: string;
  source?: string;
  guidelines_preview?: string;
}

export interface PersonaSourceData {
  gsc_cluster_ids: string[];
  gsc_query_count: number;
  gsc_total_impressions: number;
  gsc_total_clicks: number;
  representative_queries: string[];
  ga4_top_countries: string[];
  ga4_organic_sessions: number;
  ga4_conversion_rate: number | null;
  confidence_score: number;
}

export interface BuyerPersona { 
  id?: string; 
  // Display name: we map from context.role_or_identity if present (legacy name kept for UI usage)
  name?: string; 
  context?: PersonaContext;
  parameters?: PersonaParameters;
  jobs_to_be_done?: string[];
  pain_points?: string[];
  hesitations_and_risks?: string[];
  created_at?: string; // ISO timestamps (left as string – parsing left to consumers if needed)
  updated_at?: string;
  status?: 'new' | 'ready' | 'modified';
  // Persona generation source
  source?: 'ai_generated' | 'data_driven' | 'user_prompt';
  source_data?: PersonaSourceData;
  questions: PersonaQA[]; 
  raw?: unknown; 
}
export interface PromptState {
  statusByDomain: Record<string, 'idle' | 'loading' | 'succeeded' | 'failed'>;
  generatingByDomain: Record<string, boolean>;
  errorByDomain: Record<string, string | undefined>;
  personasByDomain: Record<string, BuyerPersona[]>;
  /** Tracks which `days` value is currently active per domain (set by syncPersonasDaysCache) */
  activeDaysByDomain: Record<string, number>;
}

const initialState: PromptState = {
  statusByDomain: {},
  generatingByDomain: {},
  errorByDomain: {},
  personasByDomain: {},
  activeDaysByDomain: {},
};

interface RawPersonaQuestion {
  question?: unknown;
  answers?: unknown;             // object keyed by model OR array of strings (legacy)
  // sources is a dictionary keyed by model name, each value is an array of source objects
  // { [modelName]: Array<{ uri?: string; url?: string; title?: string }> }
  sources?: unknown;
  // { [modelName]: boolean } whether the target brand/company was mentioned
  target_mentioned?: unknown;
  source?: unknown;            // 'ai_generated' | 'gsc_derived'
  id?: unknown;                // question id (UUID)
  intent?: unknown;            // 'informational' | 'transactional' (data-driven only)
  search_queries?: unknown;    // { [modelName]: string[] } query fan-out
  runs?: unknown;              // array of run entries from API
}

interface RawBuyerPersona { 
  id?: unknown; 
  name?: unknown; // legacy (may be absent now) 
  context?: unknown; // { role_or_identity, industry, environment_scale, location }
  parameters?: unknown; // { target_industry, product_service, location, source, guidelines_preview }
  jobs_to_be_done?: unknown; // string[]
  pain_points?: unknown; // string[]
  hesitations_and_risks?: unknown; // string[]
  created_at?: unknown; // ISO string
  updated_at?: unknown; // ISO string
  status?: unknown; // "new" | "ready" | "modified"
  source?: unknown; // 'ai_generated' | 'data_driven' | 'user_prompt'
  source_data?: unknown; // PersonaSourceData
  questions?: RawPersonaQuestion[]; 
}

// --------------------------------------------------------
// Normalization helpers (keep all data-shape knowledge here)
// --------------------------------------------------------

const isNonEmptyString = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;
const asString = (v: unknown): string | undefined => {
  if (typeof v === 'number') return String(v);
  return isNonEmptyString(v) ? v.trim() : undefined;
};
const isPlainObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/**
 * Normalize answers into a model-keyed record.
 * Supported input shape only:
 * - answers: { [modelName]: string }
 */
function normalizeAnswersByModel(rawQ: RawPersonaQuestion): Record<string, string> {
  const byModel: Record<string, string> = {};

  if (!isPlainObject(rawQ?.answers)) return byModel;

  const obj = rawQ.answers as Record<string, unknown>;
  for (const [model, v] of Object.entries(obj)) {
    const s = asString(v);
    if (s) byModel[model] = s;
  }
  return byModel;
}

/**
 * Normalize sources into a model-keyed dictionary of { uri?, title? }[].
 * Input shape (from API):
 *   sources: { [modelName]: Array<{ uri?: string; url?: string; title?: string }> }
 * We map url->uri, drop empty entries, and de-duplicate per model by uri/title.
 */
function normalizeSourcesByModel(rawQ: RawPersonaQuestion): Record<string, { uri?: string; title?: string }[]> {
  if (!isPlainObject(rawQ?.sources)) return {};

  const dict = rawQ.sources as Record<string, unknown>;
  const out: Record<string, { uri?: string; title?: string }[]> = {};

  for (const [model, value] of Object.entries(dict)) {
    if (!Array.isArray(value)) continue;

    const list: { uri?: string; title?: string }[] = [];
    const seen = new Set<string>(); // dedupe by key: uri||title

    for (const item of value as unknown[]) {
      if (!isPlainObject(item)) continue;
      const uri = asString((item as Record<string, unknown>).uri) ?? asString((item as Record<string, unknown>).url);
      const title = asString((item as Record<string, unknown>).title);
      if (!uri && !title) continue;

      const key = uri ?? title!;
      if (seen.has(key)) continue;
      seen.add(key);
      list.push({ uri, title });
    }

    if (list.length) out[model] = list;
  }

  return out;
}

/** Normalize search_queries into a model-keyed string[] dictionary. */
function normalizeSearchQueriesByModel(rawQ: RawPersonaQuestion): Record<string, string[]> {
  if (!isPlainObject(rawQ?.search_queries)) return {};
  const obj = rawQ.search_queries as Record<string, unknown>;
  const out: Record<string, string[]> = {};
  for (const [model, v] of Object.entries(obj)) {
    if (!Array.isArray(v)) continue;
    const strs = (v as unknown[]).map(x => (typeof x === 'string' ? x.trim() : '')).filter(Boolean);
    if (strs.length) out[model] = strs;
  }
  return out;
}

/** Normalize target_mentioned into a model-keyed boolean dictionary. */
function normalizeTargetMentionedByModel(rawQ: RawPersonaQuestion): Record<string, boolean> {
  if (!isPlainObject(rawQ?.target_mentioned)) return {};
  const obj = rawQ.target_mentioned as Record<string, unknown>;
  const out: Record<string, boolean> = {};
  for (const [model, v] of Object.entries(obj)) {
    if (typeof v === 'boolean') out[model] = v;
  }
  return out;
}

/** Normalize runs array from API into typed QARun[]. */
function normalizeRuns(rawQ: RawPersonaQuestion): QARun[] {
  if (!Array.isArray(rawQ?.runs)) return [];
  const out: QARun[] = [];
  for (const item of rawQ.runs as unknown[]) {
    if (!isPlainObject(item)) continue;
    const r = item as Record<string, unknown>;
    const model = asString(r.model) ?? '';
    const answer = asString(r.answer) ?? '';
    const targetMentioned = typeof r.target_mentioned === 'boolean' ? r.target_mentioned : false;
    const generatedAt = asString(r.generated_at) ?? '';
    // Normalize sources
    const sources: { uri?: string; title?: string }[] = [];
    if (Array.isArray(r.sources)) {
      for (const s of r.sources as unknown[]) {
        if (!isPlainObject(s)) continue;
        const sd = s as Record<string, unknown>;
        const uri = asString(sd.uri) ?? asString(sd.url);
        const title = asString(sd.title);
        if (uri || title) sources.push({ uri, title });
      }
    }
    // Normalize search queries
    let searchQueries: string[] = [];
    if (Array.isArray(r.search_queries)) {
      searchQueries = (r.search_queries as unknown[])
        .map(x => typeof x === 'string' ? x.trim() : '')
        .filter(Boolean);
    }
    out.push({ model, answer, targetMentioned, sources, searchQueries, generatedAt });
  }
  return out;
}

/** Convert a raw question into a normalized PersonaQA or null if it contains no useful content. */
function toPersonaQA(rawQ: RawPersonaQuestion): PersonaQA | null {
  const question = asString(rawQ?.question) ?? '';
  const answersByModel = normalizeAnswersByModel(rawQ);
  const sourcesByModel = normalizeSourcesByModel(rawQ);
  const targetMentionedByModel = normalizeTargetMentionedByModel(rawQ);
  const searchQueriesByModel = normalizeSearchQueriesByModel(rawQ);
  const runs = normalizeRuns(rawQ);

  const hasContent =
    isNonEmptyString(question) ||
    Object.keys(answersByModel).length > 0 ||
    Object.values(sourcesByModel).some(arr => Array.isArray(arr) && arr.length > 0);

  if (!hasContent) return null;
  const qSource = asString(rawQ?.source);
  const validSource = (qSource === 'gsc_derived' || qSource === 'ai_generated') ? qSource : undefined;
  const qIntent = asString(rawQ?.intent);
  const validIntent = (qIntent === 'informational' || qIntent === 'transactional') ? qIntent : undefined;
  return { 
    question, 
    answersByModel, 
    sourcesByModel: Object.keys(sourcesByModel).length ? sourcesByModel : undefined,
    targetMentionedByModel: Object.keys(targetMentionedByModel).length ? targetMentionedByModel : undefined,
    searchQueriesByModel: Object.keys(searchQueriesByModel).length ? searchQueriesByModel : undefined,
    runs: runs.length > 0 ? runs : undefined,
    source: validSource,
    intent: validIntent,
  };
}

// Helpers for new persona schema fields
const asStringArray = (v: unknown): string[] | undefined => {
  if (!Array.isArray(v)) return undefined;
  const out = v.map(x => (isNonEmptyString(x) ? x.trim() : undefined)).filter(Boolean) as string[];
  return out.length ? out : undefined;
};

function normalizeContext(value: unknown): PersonaContext | undefined {
  if (!isPlainObject(value)) return undefined;
  const ctx = value as Record<string, unknown>;
  const role_or_identity = asString(ctx.role_or_identity);
  const industry = asString(ctx.industry);
  const environment_scale = asString(ctx.environment_scale);
  const location = asString(ctx.location);
  const anyPresent = role_or_identity || industry || environment_scale || location;
  return anyPresent ? { role_or_identity, industry, environment_scale, location } : undefined;
}

function normalizeSourceData(value: unknown): PersonaSourceData | undefined {
  if (!isPlainObject(value)) return undefined;
  const sd = value as Record<string, unknown>;
  const gsc_cluster_ids = Array.isArray(sd.gsc_cluster_ids) ? (sd.gsc_cluster_ids as string[]) : [];
  const representative_queries = Array.isArray(sd.representative_queries) ? (sd.representative_queries as string[]) : [];
  const ga4_top_countries = Array.isArray(sd.ga4_top_countries) ? (sd.ga4_top_countries as string[]) : [];
  // If nothing meaningful, skip
  if (gsc_cluster_ids.length === 0 && representative_queries.length === 0) return undefined;
  return {
    gsc_cluster_ids,
    gsc_query_count: typeof sd.gsc_query_count === 'number' ? sd.gsc_query_count : 0,
    gsc_total_impressions: typeof sd.gsc_total_impressions === 'number' ? sd.gsc_total_impressions : 0,
    gsc_total_clicks: typeof sd.gsc_total_clicks === 'number' ? sd.gsc_total_clicks : 0,
    representative_queries,
    ga4_top_countries,
    ga4_organic_sessions: typeof sd.ga4_organic_sessions === 'number' ? sd.ga4_organic_sessions : 0,
    ga4_conversion_rate: typeof sd.ga4_conversion_rate === 'number' ? sd.ga4_conversion_rate : null,
    // confidence_score: new data is 0-100, legacy data was 0-1 — normalise to 0-100
    confidence_score: typeof sd.confidence_score === 'number'
      ? (sd.confidence_score <= 1 ? sd.confidence_score * 100 : sd.confidence_score)
      : 0,
  };
}

function normalizeProductService(value: unknown): ProductService | undefined {
  if (!isPlainObject(value)) return undefined;
  const ps = value as Record<string, unknown>;
  const name = asString(ps.name);
  const description = asString(ps.description);
  const features_and_differentiators = asStringArray(ps.features_and_differentiators);
  const pricing = asStringArray(ps.pricing);
  const anyPresent = name || description || features_and_differentiators || pricing;
  return anyPresent ? { name, description, features_and_differentiators, pricing } : undefined;
}

function normalizeParameters(value: unknown): PersonaParameters | undefined {
  if (!isPlainObject(value)) return undefined;
  const params = value as Record<string, unknown>;
  const target_industry = asString(params.target_industry);
  const product_service = normalizeProductService(params.product_service);
  const location = asString(params.location);
  const source = asString(params.source);
  const guidelines_preview = asString(params.guidelines_preview);
  const anyPresent = target_industry || product_service || location || source || guidelines_preview;
  return anyPresent ? { target_industry, product_service, location, source, guidelines_preview } : undefined;
}

/** Convert a raw buyer persona into our typed shape, normalizing each question along the way. */
function toBuyerPersona(bp: RawBuyerPersona, idx: number): BuyerPersona {
  const rawQuestions = Array.isArray(bp?.questions) ? (bp.questions as RawPersonaQuestion[]) : [];
  
  // Robust ID extraction: check strictly string or number id, or _id, or uuid
  let extractedId = asString(bp?.id);
  if (!extractedId) {
    // Try other common patterns
    const anyBp = bp as Record<string, unknown>;
    extractedId = asString(anyBp?._id) ?? asString(anyBp?.uuid) ?? asString(anyBp?.uid);
  }
  // Fallback to index if absolutely nothing found (likely to fail updates, but prevents crash)
  const id = extractedId ?? String(idx);

  const questions = rawQuestions
    .map(toPersonaQA)
    .map((q, qIdx) => (q ? { ...q, qaId: `${id}:${qIdx}` } as PersonaQA : null))
    .filter(Boolean) as PersonaQA[];

  const context = normalizeContext(bp?.context);
  const parameters = normalizeParameters(bp?.parameters);
  const jobs_to_be_done = asStringArray(bp?.jobs_to_be_done);
  const pain_points = asStringArray(bp?.pain_points);
  const hesitations_and_risks = asStringArray(bp?.hesitations_and_risks);
  const created_at = asString(bp?.created_at);
  const updated_at = asString(bp?.updated_at);
  const statusStr = asString(bp?.status);
  const status = (statusStr === 'new' || statusStr === 'ready' || statusStr === 'modified') ? statusStr : 'new';

  // Derive display name: prefer explicit name (legacy), else role_or_identity from context.
  const name = asString(bp?.name) ?? context?.role_or_identity;

  // Source tracking for data-driven personas
  const sourceStr = asString(bp?.source);
  const personaSource = (sourceStr === 'data_driven' || sourceStr === 'ai_generated' || sourceStr === 'user_prompt') ? sourceStr : undefined;
  const source_data = normalizeSourceData(bp?.source_data);

  return { 
    id, 
    name, 
    context, 
    parameters,
    jobs_to_be_done, 
    pain_points, 
    hesitations_and_risks, 
    created_at, 
    updated_at,
    status,
    source: personaSource,
    source_data,
    questions, 
    raw: bp 
  };
}

export const fetchBuyerPersonas = createAsyncThunk<{
  domainId: string; days: number; personas: BuyerPersona[];
}, string | { domainId: string; force?: boolean; days?: number }, { state: RootState }>(
  'prompt/fetchBuyerPersonas',
  async (arg, { rejectWithValue }) => {
  const domainId = typeof arg === 'string' ? arg : arg.domainId;
  const days = typeof arg === 'string' ? 7 : (arg.days ?? 7);
  try {
    const res = await apiClient.get(`/v1/domains/${domainId}/buyer-personas`, { params: { days } });
    const payload: unknown = res.data;
    const dataProp = (payload && typeof payload === 'object' && (payload as Record<string, unknown>).hasOwnProperty('data'))
      ? (payload as { data: unknown }).data
      : payload;
    const arr: RawBuyerPersona[] = Array.isArray(dataProp) ? (dataProp as RawBuyerPersona[]) : [];
    const personas: BuyerPersona[] = arr.map((bp: RawBuyerPersona, idx: number) => toBuyerPersona(bp, idx));
    return { domainId, days, personas };
  } catch (err: unknown) {
    console.error('fetchBuyerPersonas error:', err);
    const maybeResp = (err as { response?: { data?: { message?: unknown } } }).response;
    const msg = maybeResp?.data?.message;
    const errorMsg = typeof msg === 'string' 
      ? msg 
      : (err instanceof Error ? err.message : 'Failed to load buyer personas');
    return rejectWithValue(errorMsg);
  }
}, {
  condition: (arg, { getState }) => {
    const domainId = typeof arg === 'string' ? arg : arg.domainId;
    const force = typeof arg === 'string' ? false : arg.force;
    const days = typeof arg === 'string' ? 7 : (arg.days ?? 7);
    const ck = `${domainId}:${days}`;

    const { prompt } = getState();
    const st = prompt.statusByDomain[ck];

    // Block if already loading this exact (domain, days) combo
    if (st === 'loading') return false;

    // Force bypasses cache
    if (force) return true;

    // Cache hit — skip if we have data for this (domain, days)
    const list = prompt.personasByDomain[ck];
    if (st === 'succeeded' && list && list.length > 0) return false;

    return true;
  }
});

export const generateBuyerPersona = createAsyncThunk<{
  domainId: string;
}, { domainId: string; prompt: string }, { state: RootState }>(
  'prompt/generateBuyerPersona',
  async ({ domainId, prompt }, { rejectWithValue }) => {
    try {
      // Just start the job
      await apiClient.post(`/v1/domains/${domainId}/buyer-personas/generate`, { prompt });
      return { domainId };
    } catch (err: unknown) {
      console.error('generateBuyerPersona error:', err);
      const maybeResp = (err as { response?: { data?: { message?: unknown } } }).response;
      const msg = maybeResp?.data?.message;
      const errorMsg = typeof msg === 'string' 
        ? msg 
        : (err instanceof Error ? err.message : 'Failed to generate persona');
      return rejectWithValue(errorMsg);
    }
  }
);

export const createBuyerPersona = createAsyncThunk<{
  domainId: string; persona: BuyerPersona;
}, { domainId: string; persona: BuyerPersona }, { state: RootState }>(
  'prompt/createBuyerPersona',
  async ({ domainId, persona }, { rejectWithValue }) => {
    try {
      const payload = {
        context: persona.context,
        parameters: persona.parameters,
        jobs_to_be_done: persona.jobs_to_be_done,
        pain_points: persona.pain_points,
        hesitations_and_risks: persona.hesitations_and_risks,
        name: persona.name
      };
      
      const res = await apiClient.post(`/v1/domains/${domainId}/buyer-personas`, payload);
      const raw = res.data.data || res.data;
      const createdPersona = toBuyerPersona(raw, 0);
      return { domainId, persona: createdPersona };
    } catch (err: unknown) {
      console.error('createBuyerPersona error:', err);
      const maybeResp = (err as { response?: { data?: { message?: unknown } } }).response;
      const msg = maybeResp?.data?.message;
      const errorMsg = typeof msg === 'string' 
        ? msg 
        : (err instanceof Error ? err.message : 'Failed to create buyer persona');
      return rejectWithValue(errorMsg);
    }
  }
);

export const updateBuyerPersona = createAsyncThunk<{
  domainId: string; persona: BuyerPersona;
}, { domainId: string; persona: BuyerPersona }, { state: RootState }>(
  'prompt/updateBuyerPersona',
  async ({ domainId, persona }, { rejectWithValue }) => {
    try {
      if (!persona.id) throw new Error('Persona ID is missing');
      const payload = {
        context: persona.context,
        parameters: persona.parameters,
        jobs_to_be_done: persona.jobs_to_be_done,
        pain_points: persona.pain_points,
        hesitations_and_risks: persona.hesitations_and_risks,
        name: persona.name // if backend supports updating name
      };
      
      const res = await apiClient.put(`/v1/domains/${domainId}/buyer-personas/${persona.id}`, payload);
      // Assuming response returns the updated persona
      return { domainId, persona: { ...persona, ...res.data } };
    } catch (err: unknown) {
      console.error('updateBuyerPersona error:', err);
      const maybeResp = (err as { response?: { data?: { message?: unknown } } }).response;
      const msg = maybeResp?.data?.message;
      const errorMsg = typeof msg === 'string' 
        ? msg 
        : (err instanceof Error ? err.message : 'Failed to update buyer persona');
      return rejectWithValue(errorMsg);
    }
  }
);

export const saveBuyerPersonas = createAsyncThunk<{
  domainId: string; personas: BuyerPersona[];
}, { domainId: string; personas: BuyerPersona[] }, { state: RootState }>(
  'prompt/saveBuyerPersonas',
  async ({ domainId, personas }, { rejectWithValue }) => {
    try {
      // Clean up local-only fields before sending
      const personasList = personas.map(p => {
        // Map to BuyerPersonaItemRequest structure
        const clean: any = {
           context: p.context || {},
           jobs_to_be_done: p.jobs_to_be_done || [],
           pain_points: p.pain_points || [],
           hesitations_and_risks: p.hesitations_and_risks || [],
           profile_title: p.name || 'Untitled Persona',
        };
        
        // Only include parameters if present and non-empty
        if (p.parameters && Object.keys(p.parameters).length > 0) {
          clean.parameters = p.parameters;
        }
        
        // If it has a real persistent ID, include it
        if (p.id && !p.id.startsWith('new_') && !/^\d+$/.test(p.id)) {
          clean.id = p.id;
        }

        return clean;
      });

      // Wrap in expected UpdateBuyerPersonasRequest structure: { personas: List[...] }
      const payload = { personas: personasList };

      console.log('saveBuyerPersonas payload:', payload);

      const res = await apiClient.put(`/v1/domains/${domainId}/buyer-personas`, payload);
      
      // Expected response: the full updated list of personas
      const rawList = res.data.data || res.data;
      const updatedPersonas = Array.isArray(rawList) ? rawList.map((r, i) => toBuyerPersona(r, i)) : [];
      
      return { domainId, personas: updatedPersonas };

    } catch (err: unknown) {
      console.error('saveBuyerPersonas error:', err);
      const maybeResp = (err as { response?: { data?: { message?: unknown } } }).response;
      const msg = maybeResp?.data?.message;
      const errorMsg = typeof msg === 'string' 
        ? msg 
        : (err instanceof Error ? err.message : 'Failed to save buyer personas');
      return rejectWithValue(errorMsg);
    }
  }
);

export const savePrompts = createAsyncThunk<{
  domainId: string;
}, { domainId: string; personas: BuyerPersona[] }, { state: RootState }>(
  'prompt/savePrompts',
  async ({ domainId, personas }, { rejectWithValue }) => {
    try {
      const promptsPayload = personas.map(p => ({
        buyer_persona_id: p.id,
        questions: (p.questions || []).map(q => ({
          id: q.qaId, // Map qaId to id
          question: q.question,
        }))
      }));

      const payload = { prompts: promptsPayload };

      await apiClient.put(`/v1/domains/${domainId}/prompts`, payload);
      
      return { domainId };

    } catch (err: unknown) {
      console.error('savePrompts error:', err);
      const maybeResp = (err as { response?: { data?: { message?: unknown } } }).response;
      const msg = maybeResp?.data?.message;
      const errorMsg = typeof msg === 'string' 
        ? msg 
        : (err instanceof Error ? err.message : 'Failed to save prompts');
      return rejectWithValue(errorMsg);
    }
  }
);

const promptSlice = createSlice({
  name: 'prompt',
  initialState,
  reducers: {
    syncPersonasDaysCache(state, action: PayloadAction<{ domainId: string; days: number }>) {
      const { domainId, days } = action.payload;
      state.activeDaysByDomain[domainId] = days;
      const ck = `${domainId}:${days}`;
      if (state.personasByDomain[ck]) {
        state.personasByDomain[domainId] = state.personasByDomain[ck];
        state.statusByDomain[domainId] = 'succeeded';
        state.errorByDomain[domainId] = undefined;
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchBuyerPersonas.pending, (state, action) => {
        const arg = action.meta.arg;
        const domainId = typeof arg === 'string' ? arg : arg.domainId;
        const days = typeof arg === 'string' ? 7 : (arg.days ?? 7);
        const ck = `${domainId}:${days}`;
        // Only set loading if no cached data (prevents flicker during prefetch)
        if (!state.personasByDomain[ck] || state.personasByDomain[ck].length === 0) {
            state.statusByDomain[ck] = 'loading';
        }
        state.errorByDomain[ck] = undefined;
        // Only mirror to simple key if this is the active time range
        const activeDays = state.activeDaysByDomain[domainId];
        if (activeDays === undefined || days === activeDays) {
          if (!state.personasByDomain[domainId] || state.personasByDomain[domainId].length === 0) {
              state.statusByDomain[domainId] = 'loading';
          }
          state.errorByDomain[domainId] = undefined;
        }
      })
      .addCase(fetchBuyerPersonas.fulfilled, (state, action) => {
        const { domainId, days, personas } = action.payload;
        const ck = `${domainId}:${days}`;
        state.statusByDomain[ck] = 'succeeded';
        state.errorByDomain[ck] = undefined;
        state.personasByDomain[ck] = personas;
        // Only mirror to simple key if this matches the active time range
        const activeDays = state.activeDaysByDomain[domainId];
        if (activeDays === undefined || days === activeDays) {
          state.statusByDomain[domainId] = 'succeeded';
          state.errorByDomain[domainId] = undefined;
          state.personasByDomain[domainId] = personas;
        }
      })
      .addCase(fetchBuyerPersonas.rejected, (state, action) => {
        const arg = action.meta.arg;
        const domainId = typeof arg === 'string' ? arg : arg.domainId;
        const days = typeof arg === 'string' ? 7 : (arg.days ?? 7);
        const ck = `${domainId}:${days}`;
        const msg = typeof action.payload === 'string' ? action.payload : 'Unknown error';
        state.statusByDomain[ck] = 'failed';
        state.errorByDomain[ck] = msg;
        const activeDays = state.activeDaysByDomain[domainId];
        if (activeDays === undefined || days === activeDays) {
          state.statusByDomain[domainId] = 'failed';
          state.errorByDomain[domainId] = msg;
        }
      })
      .addCase(generateBuyerPersona.pending, (state, action) => {
        const { domainId } = action.meta.arg;
        state.generatingByDomain[domainId] = true;
      })
      .addCase(generateBuyerPersona.fulfilled, (state, action) => {
        const { domainId } = action.payload;
        // Job started, waiting for polling to pick up results
        state.generatingByDomain[domainId] = false;
      })
      .addCase(generateBuyerPersona.rejected, (state, action) => {
        const { domainId } = action.meta.arg;
        state.generatingByDomain[domainId] = false;
        // Optionally store error
        const msg = typeof action.payload === 'string' ? action.payload : 'Generation failed';
        state.errorByDomain[domainId] = msg;
      })
      .addCase(createBuyerPersona.fulfilled, (state, action) => {
        const { domainId, persona } = action.payload;
        if (state.personasByDomain[domainId]) {
          state.personasByDomain[domainId].push(persona);
        } else {
          state.personasByDomain[domainId] = [persona];
        }
      })
      .addCase(updateBuyerPersona.fulfilled, (state, action) => {
        const { domainId, persona } = action.payload;
        if (state.personasByDomain[domainId]) {
          const index = state.personasByDomain[domainId].findIndex(p => p.id === persona.id);
          if (index !== -1) {
            state.personasByDomain[domainId][index] = persona;
          }
        }
      })
      .addCase(saveBuyerPersonas.fulfilled, (state, action) => {
        const { domainId, personas } = action.payload;
        state.personasByDomain[domainId] = personas;
        state.statusByDomain[domainId] = 'succeeded';
        state.errorByDomain[domainId] = undefined;
      })
      .addCase(savePrompts.fulfilled, (state, action) => {
        const { domainId } = action.payload;
        state.statusByDomain[domainId] = 'succeeded';
        state.errorByDomain[domainId] = undefined;
      });
  }
});

export const { syncPersonasDaysCache } = promptSlice.actions;
export default promptSlice.reducer;
